import { useEffect, useState } from "react";
import { deleteAIImportSettings, getAIImportSettings, updateAIImportSettings } from "lib/aiImport";
import handlePopupFlagError from "lib/errors/handlePopupFlagError";

export const AIImportSettingsPage = () => {
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [apiKey, setApiKey] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getAIImportSettings()
      .then((settings) => {
        if (!cancelled) setConfigured(settings.configured);
      })
      .catch((err) => {
        handlePopupFlagError(err);
        if (!cancelled) setConfigured(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async () => {
    const trimmed = apiKey.trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      const settings = await updateAIImportSettings({ geminiApiKey: trimmed });
      setConfigured(settings.configured);
      // The key is write-only; drop it from memory once stored.
      setApiKey("");
    } catch (err) {
      handlePopupFlagError(err);
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    setBusy(true);
    try {
      const settings = await deleteAIImportSettings();
      setConfigured(settings.configured);
    } catch (err) {
      handlePopupFlagError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 max-w-2xl">
      <h1 className="text-xl font-semibold text-gray-900">AI certificate import</h1>
      <p className="mt-2 text-sm text-gray-600">
        Add a Google Gemini API key to let this workspace read calibration certificates
        automatically. Until a key is saved, the import option stays hidden.
      </p>

      {configured !== null && (
        <div className="mt-6 flex items-center gap-2">
          <span className="text-sm text-gray-500">Status:</span>
          <span
            className={
              configured
                ? "rounded-full bg-green-50 px-2.5 py-0.5 text-xs font-medium text-green-700"
                : "rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600"
            }
          >
            {configured ? "Configured" : "Not configured"}
          </span>
        </div>
      )}

      <div className="mt-6 flex flex-col gap-2">
        <label htmlFor="gemini-api-key" className="text-sm font-medium text-gray-700">
          Gemini API key
        </label>
        <input
          id="gemini-api-key"
          type="password"
          autoComplete="off"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder={configured ? "Enter a new key to replace the stored one" : "Paste your API key"}
          className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>

      <div className="mt-4 flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={busy}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
        >
          Save key
        </button>
        {configured && (
          <button
            onClick={handleRemove}
            disabled={busy}
            className="text-sm text-red-600 hover:text-red-700 disabled:opacity-60"
          >
            Remove key
          </button>
        )}
      </div>
    </div>
  );
};
